import { useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import { toast } from "react-hot-toast";
import axiosInstance from "../utils/axiosInstance";
import { FiUser, FiSearch, FiMessageCircle, FiCheck } from "react-icons/fi";
import styled from "styled-components";

const AfricanCountries = [
  "Algeria", "Angola", "Benin", "Botswana", "Burkina Faso", "Burundi",
  "Cabo Verde", "Cameroon", "Central African Republic", "Chad", "Comoros",
  "Democratic Republic of the Congo", "Djibouti", "Egypt", "Equatorial Guinea",
  "Eritrea", "Eswatini", "Ethiopia", "Gabon", "Gambia", "Ghana", "Guinea",
  "Guinea-Bissau", "Ivory Coast", "Kenya", "Lesotho", "Liberia", "Libya",
  "Madagascar", "Malawi", "Mali", "Mauritania", "Mauritius", "Morocco",
  "Mozambique", "Namibia", "Niger", "Nigeria", "Republic of the Congo", "Rwanda",
  "São Tomé and Príncipe", "Senegal", "Seychelles", "Sierra Leone", "Somalia",
  "South Africa", "South Sudan", "Sudan", "Tanzania", "Togo", "Tunisia",
  "Uganda", "Zambia", "Zimbabwe",
];

const LEARNING_GOALS = [
  'Get a new job', 'Upskill in current role', 'Start a business',
  'Learn a new skill', 'Get certified', 'Switch careers',
  'Improve digital literacy', 'Build a portfolio', 'Freelancing',
];

const INTERESTS = [
  'Web Development', 'Mobile Development', 'Backend Development', 'Frontend Development',
  'Full-Stack Development', 'DevOps', 'Cloud Computing', 'Cybersecurity',
  'Data Science', 'Machine Learning', 'Artificial Intelligence',
  'UI/UX Design', 'Graphic Design', 'Product Management', 'Digital Marketing',
];

const INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Education', 'E-commerce',
  'Media & Entertainment', 'Agriculture', 'Government', 'NGO/Non-profit',
  'Manufacturing', 'Telecoms', 'Consulting',
];

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 24px;
    border: none;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    background: #ffffff;
  }
  .modal-header {
    background: linear-gradient(135deg, rgba(0, 170, 135, 0.08) 0%, rgba(0, 170, 135, 0.02) 100%);
    border-bottom: 1px solid #F1F5F9;
    padding: 20px 28px;
  }
  .modal-body {
    padding: 28px;
  }
  .form-control, .form-select {
    border-radius: 10px;
    border: 1.5px solid #E2E8F0;
    height: 46px;
    font-size: 0.9rem;
    color: #0F172A;
    transition: all 0.2s ease-in-out;
    &:focus {
      border-color: #00AA87;
      box-shadow: 0 0 0 4px rgba(0, 170, 135, 0.15);
    }
  }
  textarea.form-control {
    height: auto !important;
  }
  .form-label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #334155;
    margin-bottom: 6px;
  }
`;

const StepBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const StepDot = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${props => props.active ? '#00AA87' : props.completed ? '#00AA87' : '#E2E8F0'};
  opacity: ${props => props.active ? 1 : props.completed ? 0.6 : 0.4};
  transition: all 0.3s ease;
`;

const Chip = styled.button`
  background: ${props => props.selected ? 'rgba(0, 170, 135, 0.12)' : '#F8FAFC'};
  color: ${props => props.selected ? '#00AA87' : '#475569'};
  border: 1.5px solid ${props => props.selected ? '#00AA87' : '#E2E8F0'};
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: ${props => props.selected ? '600' : '500'};
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #00AA87;
    background: rgba(0, 170, 135, 0.08);
  }
`;

export default function CompleteProfileModal({ show, onComplete }) {
  const [step, setStep] = useState(1); // 1: profile, 2: goals, 3: availability, 4: path
  const [savedData, setSavedData] = useState({});
  const [loading, setLoading] = useState(false);
  const avatarRef = useRef();

  // Step 1 Form: Profile Info
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");
  const [gender, setGender] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [errors, setErrors] = useState({});

  // Step 2 Form: Goals & Interests
  const [primaryGoal, setPrimaryGoal] = useState("");
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [targetIndustries, setTargetIndustries] = useState([]);
  const [targetRoles, setTargetRoles] = useState("");

  // Step 3 Form: Availability & Learning Preferences
  const [weeklyHours, setWeeklyHours] = useState("");
  const [motivationLevel, setMotivationLevel] = useState("");
  const [device, setDevice] = useState("");
  const [preferredLearningStyle, setPreferredLearningStyle] = useState("");
  const [budget, setBudget] = useState("");

  const validateStep1 = () => {
    const e = {};
    if (!fullName.trim()) e.fullName = "Full name is required.";
    if (!phoneNumber) {
      e.phoneNumber = "Phone number is required.";
    } else if (!/^\+?\d[\d\s-]{7,}$/.test(phoneNumber)) {
      e.phoneNumber = "Enter a valid phone number (e.g. +234 XXX XXX XXXX).";
    }
    if (!country) e.country = "Please select your country.";
    if (!gender) e.gender = "Please select your gender.";
    return e;
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setAvatarFile(file);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  // Submit Step 1: Personal Profile
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const errs = validateStep1();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("fullName", fullName);
      formData.append("phoneNumber", phoneNumber);
      formData.append("country", country);
      formData.append("gender", gender);
      if (bio) formData.append("bio", bio);
      if (location) formData.append("location", location);
      if (avatarFile) formData.append("profilePicture", avatarFile);

      await axiosInstance.patch("/user/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const data = { fullName, phoneNumber, country, gender, bio, location };
      setSavedData(prev => ({ ...prev, ...data }));
      toast.success("Profile saved!");
      setStep(2); // move to goals step
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save profile.");
    } finally {
      setLoading(false);
    }
  };

  // Submit Step 2: Goals & Interests
  const handleGoalsSubmit = async (isSkip = false) => {
    if (!isSkip && (primaryGoal || selectedInterests.length > 0 || targetIndustries.length > 0 || targetRoles)) {
      setLoading(true);
      try {
        await axiosInstance.patch("/dashboard/learner-profile", {
          primary_goal: primaryGoal,
          interests: selectedInterests,
          target_industries: targetIndustries,
          target_roles: targetRoles ? targetRoles.split(',').map(s => s.trim()).filter(Boolean) : [],
        });
        toast.success("Goals & interests saved!");
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not save goals.");
      } finally {
        setLoading(false);
      }
    }
    setStep(3); // move to availability step
  };

  // Submit Step 3: Availability & Preferences
  const handleAvailabilitySubmit = async (isSkip = false) => {
    if (!isSkip && (weeklyHours || motivationLevel || device || preferredLearningStyle || budget)) {
      setLoading(true);
      try {
        await axiosInstance.patch("/dashboard/learner-profile", {
          weekly_hours: weeklyHours ? Number(weeklyHours) : undefined,
          motivation_level: motivationLevel || undefined,
          device: device || undefined,
          preferred_learning_style: preferredLearningStyle || undefined,
          budget: budget || undefined,
        });
        toast.success("Availability preferences saved!");
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not save availability.");
      } finally {
        setLoading(false);
      }
    }
    setStep(4); // move to final path screen
  };

  const toggleInterest = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const toggleIndustry = (ind) => {
    setTargetIndustries(prev =>
      prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]
    );
  };

  return (
    <StyledModal show={show} backdrop="static" keyboard={false} centered size="md">
      <Modal.Header>
        <div style={{ width: '100%' }}>
          <StepBadge>
            <StepDot active={step === 1} completed={step > 1} />
            <StepDot active={step === 2} completed={step > 2} />
            <StepDot active={step === 3} completed={step > 3} />
            <StepDot active={step === 4} completed={step > 4} />
          </StepBadge>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Modal.Title style={{ color: "#0F172A", fontWeight: 800, fontSize: "1.2rem", marginBottom: "2px" }}>
                {step === 1 && "1. Personal Profile"}
                {step === 2 && "2. Learning Goals & Interests"}
                {step === 3 && "3. Availability & Preferences"}
                {step === 4 && "4. You're All Set!"}
              </Modal.Title>
              <p style={{ color: "#64748B", fontSize: "0.82rem", margin: 0 }}>
                {step === 1 && "A few core details to set up your learner identity."}
                {step === 2 && "Help us tailor recommendations to your ambitions."}
                {step === 3 && "Tell us how and when you like to learn."}
                {step === 4 && "Choose how you would like to begin."}
              </p>
            </div>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#00AA87', background: 'rgba(0,170,135,0.1)', padding: '4px 10px', borderRadius: 12 }}>
              Step {step} of 4
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* ── Step 1: Personal Profile Form ── */}
        {step === 1 && (
          <div>
            <div className="d-flex justify-content-center mb-4">
              <div
                onClick={() => avatarRef.current?.click()}
                style={{ cursor: "pointer", display: "inline-block" }}
                title="Click to upload profile photo"
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    style={{
                      width: 84,
                      height: 84,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid #00AA87",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 84,
                      height: 84,
                      borderRadius: "50%",
                      background: "rgba(0, 170, 135, 0.08)",
                      border: "2px dashed #00AA87",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiUser size={34} color="#00AA87" />
                  </div>
                )}
                <input
                  ref={avatarRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleAvatarChange}
                />
              </div>
            </div>

            <Form onSubmit={handleProfileSubmit} noValidate>
              <Form.Group className="mb-3">
                <Form.Label>Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Ciroma Chukwuma"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); setErrors(p => ({ ...p, fullName: "" })); }}
                  isInvalid={!!errors.fullName}
                />
                <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number *</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="+234 XXX XXX XXXX"
                  value={phoneNumber}
                  onChange={(e) => { setPhoneNumber(e.target.value); setErrors(p => ({ ...p, phoneNumber: "" })); }}
                  isInvalid={!!errors.phoneNumber}
                />
                <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
              </Form.Group>

              <Row className="g-3 mb-3">
                <Col sm={7}>
                  <Form.Group>
                    <Form.Label>Country *</Form.Label>
                    <Form.Select
                      value={country}
                      onChange={(e) => { setCountry(e.target.value); setErrors(p => ({ ...p, country: "" })); }}
                      isInvalid={!!errors.country}
                    >
                      <option value="">Select country</option>
                      {AfricanCountries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group>
                    <Form.Label>Gender *</Form.Label>
                    <Form.Select
                      value={gender}
                      onChange={(e) => { setGender(e.target.value); setErrors(p => ({ ...p, gender: "" })); }}
                      isInvalid={!!errors.gender}
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Location <span style={{ color: "#94A3B8", fontWeight: 400 }}>(optional)</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Lagos, Nigeria"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Bio <span style={{ color: "#94A3B8", fontWeight: 400 }}>(optional)</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Tell us a little about yourself..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </Form.Group>

              <Button
                type="submit"
                className="w-100"
                disabled={loading}
                style={{
                  background: "linear-gradient(135deg, #00AA87 0%, #00886C 100%)",
                  border: "none",
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  height: 46,
                  color: "#ffffff",
                }}
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Save Profile & Continue"}
              </Button>
            </Form>
          </div>
        )}

        {/* ── Step 2: Goals & Interests ── */}
        {step === 2 && (
          <div>
            <Form.Group className="mb-4">
              <Form.Label>Primary Learning Goal</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {LEARNING_GOALS.map(goal => (
                  <Chip
                    key={goal}
                    type="button"
                    selected={primaryGoal === goal}
                    onClick={() => setPrimaryGoal(p => p === goal ? '' : goal)}
                  >
                    {primaryGoal === goal && <FiCheck size={13} />} {goal}
                  </Chip>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Interests in Learning</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {INTERESTS.map(interest => (
                  <Chip
                    key={interest}
                    type="button"
                    selected={selectedInterests.includes(interest)}
                    onClick={() => toggleInterest(interest)}
                  >
                    {selectedInterests.includes(interest) && <FiCheck size={13} />} {interest}
                  </Chip>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Target Industries</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {INDUSTRIES.map(ind => (
                  <Chip
                    key={ind}
                    type="button"
                    selected={targetIndustries.includes(ind)}
                    onClick={() => toggleIndustry(ind)}
                  >
                    {targetIndustries.includes(ind) && <FiCheck size={13} />} {ind}
                  </Chip>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Target Roles <span style={{ color: "#94A3B8", fontWeight: 400 }}>(comma-separated)</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Frontend Developer, Data Analyst"
                value={targetRoles}
                onChange={e => setTargetRoles(e.target.value)}
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setStep(1)}
                style={{ borderRadius: 12, fontWeight: 600, width: '90px' }}
              >
                Back
              </Button>
              <Button
                variant="light"
                onClick={() => handleGoalsSubmit(true)}
                style={{ borderRadius: 12, fontWeight: 600, color: '#64748B', flex: 1 }}
              >
                Skip for now
              </Button>
              <Button
                onClick={() => handleGoalsSubmit(false)}
                disabled={loading}
                style={{
                  background: "linear-gradient(135deg, #00AA87 0%, #00886C 100%)",
                  border: "none",
                  borderRadius: 12,
                  fontWeight: 700,
                  color: "#ffffff",
                  flex: 1.5,
                }}
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Save & Continue"}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Availability & Preferences ── */}
        {step === 3 && (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>Hours Available Per Week</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={168}
                placeholder="e.g. 10"
                value={weeklyHours}
                onChange={e => setWeeklyHours(e.target.value)}
              />
            </Form.Group>

            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>Motivation Level</Form.Label>
                  <Form.Select value={motivationLevel} onChange={e => setMotivationLevel(e.target.value)}>
                    <option value="">Select</option>
                    <option value="low">Low – Need encouragement</option>
                    <option value="medium">Medium – Motivated</option>
                    <option value="high">High – Highly driven</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>Preferred Device</Form.Label>
                  <Form.Select value={device} onChange={e => setDevice(e.target.value)}>
                    <option value="">Select</option>
                    <option value="phone">Mobile Phone</option>
                    <option value="pc">PC / Laptop</option>
                    <option value="both">Both</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>Learning Style</Form.Label>
                  <Form.Select value={preferredLearningStyle} onChange={e => setPreferredLearningStyle(e.target.value)}>
                    <option value="">Select</option>
                    <option value="video">Video-based</option>
                    <option value="reading">Reading / Articles</option>
                    <option value="hands-on">Hands-on / Projects</option>
                    <option value="live">Live classes</option>
                    <option value="mixed">Mixed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>Budget Range</Form.Label>
                  <Form.Select value={budget} onChange={e => setBudget(e.target.value)}>
                    <option value="">Select</option>
                    <option value="free">Free only</option>
                    <option value="low">Low (under $20)</option>
                    <option value="medium">Medium ($20–$100)</option>
                    <option value="high">High ($100+)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setStep(2)}
                style={{ borderRadius: 12, fontWeight: 600, width: '90px' }}
              >
                Back
              </Button>
              <Button
                variant="light"
                onClick={() => handleAvailabilitySubmit(true)}
                style={{ borderRadius: 12, fontWeight: 600, color: '#64748B', flex: 1 }}
              >
                Skip for now
              </Button>
              <Button
                onClick={() => handleAvailabilitySubmit(false)}
                disabled={loading}
                style={{
                  background: "linear-gradient(135deg, #00AA87 0%, #00886C 100%)",
                  border: "none",
                  borderRadius: 12,
                  fontWeight: 700,
                  color: "#ffffff",
                  flex: 1.5,
                }}
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Save & Finish"}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 4: Path Choice ── */}
        {step === 4 && (
          <div style={{ padding: '16px 8px' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(0,170,135,0.15) 0%, rgba(0,170,135,0.05) 100%)',
                border: '2px solid #00AA87',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, marginBottom: 12,
              }}>🎉</div>
              <h5 style={{ fontWeight: 800, color: '#0F172A', marginBottom: 6 }}>
                Your onboarding is complete!
              </h5>
              <p style={{ color: '#64748B', fontSize: '0.86rem', margin: 0 }}>
                Select how you'd like to proceed:
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <button
                onClick={() => onComplete({ ...savedData, path: 'help' })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  background: '#fff', border: '2px solid #E2E8F0',
                  borderRadius: 16, padding: '18px 20px',
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#00AA87'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,170,135,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: 'rgba(0,170,135,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FiSearch size={20} color="#00AA87" />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.94rem', marginBottom: 2 }}>
                    Request Help
                  </div>
                  <div style={{ color: '#64748B', fontSize: '0.8rem' }}>
                    Browse courses and request funding support
                  </div>
                </div>
              </button>

              <button
                onClick={() => onComplete({ ...savedData, path: 'moji' })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  background: 'linear-gradient(135deg, rgba(0,170,135,0.06) 0%, rgba(0,170,135,0.02) 100%)',
                  border: '2px solid #00AA87',
                  borderRadius: 16, padding: '18px 20px',
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,170,135,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: 'rgba(0,170,135,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FiMessageCircle size={20} color="#00AA87" />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#00AA87', fontSize: '0.94rem', marginBottom: 2 }}>
                    Talk to Moji
                  </div>
                  <div style={{ color: '#64748B', fontSize: '0.8rem' }}>
                    Chat with Moji for personalized course recommendations
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
    </StyledModal>
  );
}
